import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AdminPolicyEndorsementQueueFilters,
  AdministrativeObservedSgaComposition,
  ApiSuccess,
  CreateEndorsementOverrideInput,
  MyPolicyEndorsementQueueFilters,
  PolicyEndorsementDetail,
  PolicyEndorsementOverride,
  PolicyEndorsementQueueRow,
  QueueFilterOption,
  ReconcileEndorsementBillingInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { createQueueListingHook } from './queue/createQueueListingHook';

type QueuePayload = { count: number; rows: PolicyEndorsementQueueRow[] };

export const useEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  AdminPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.admin(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.adminList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

export const useMyEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  MyPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.mine(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.mineList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/policy-endorsements/mine', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

export function useEndorsementDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.detail(id ?? ''),
    queryFn: async (): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.get<ApiSuccess<PolicyEndorsementDetail>>(
        `/policy-endorsements/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
  });
}

export async function fetchEndorsementConsultantOptions(
  search: string,
): Promise<QueueFilterOption[]> {
  const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
    params: { search: search || undefined, page: 1, limit: 100 },
  });
  const unique = new Map<string, string>();
  for (const row of response.data.data.rows) {
    if (row.consultantProfileId && row.consultantName) {
      unique.set(row.consultantProfileId, row.consultantName);
    }
  }
  return [...unique].map(([value, label]) => ({ value, label }));
}

export const usePolicyEndorsement = useEndorsementDetail;

export function useEndorsementFinancialOrigin(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.financialOrigin(id ?? ''),
    queryFn: async (): Promise<AdministrativeObservedSgaComposition> => {
      const response = await apiClient.get<ApiSuccess<AdministrativeObservedSgaComposition>>(
        `/admin/policy-endorsements/${id}/financial-origin`,
      );
      return response.data.data;
    },
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}

type OverridePayload = { count: number; rows: PolicyEndorsementOverride[] };

export function useEndorsementOverrides(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.overrides(id ?? ''),
    queryFn: async (): Promise<OverridePayload> => {
      const response = await apiClient.get<ApiSuccess<OverridePayload>>(
        `/admin/policy-endorsements/${id}/overrides`,
        { params: { page: 1, limit: 100 } },
      );
      return response.data.data;
    },
    enabled: Boolean(id) && enabled,
  });
}

function useInvalidateEndorsementOperations() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.detail(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.overrides(id) });
  };
}

export function useCreateEndorsementOverride() {
  const invalidate = useInvalidateEndorsementOperations();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: CreateEndorsementOverrideInput;
    }): Promise<PolicyEndorsementOverride> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementOverride>>(
        `/admin/policy-endorsements/${id}/overrides`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => invalidate(variables.id),
  });
}

export function useRevokeEndorsementOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      overrideId,
    }: {
      endorsementId: string;
      overrideId: string;
    }): Promise<PolicyEndorsementOverride> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementOverride>>(
        `/admin/policy-endorsement-overrides/${overrideId}/revoke`,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.policyEndorsements.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.policyEndorsements.overrides(variables.endorsementId),
      });
    },
  });
}

export function useReconcileEndorsementBilling() {
  const invalidate = useInvalidateEndorsementOperations();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: ReconcileEndorsementBillingInput;
    }): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.post<ApiSuccess<PolicyEndorsementDetail>>(
        `/admin/policy-endorsements/${id}/reconciliation`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (data) => invalidate(data.id),
  });
}
